import { useBasket } from "hooks/useBasket";
import { ServiceName } from "reduxStore/services/api-v2/orders";
import { isInboundServiceName, isOutboundServiceName } from "types/basket";
import { DivProps } from "types/dom";


const MAX_LABELS = 40;

type LabelQuantitySelectorProps = {
  service: ServiceName;
  charityId: number;
  tier: string;
  recipientId?: string;
  // What is being counted, read out by screen readers, e.g. "5kg Evri labels"
  label?: string;
  // Border colour, so the stepper can follow the delivery method's colour
  borderClass?: string;
} & DivProps

const Arrow = ({ up }: { up?: boolean }) => <svg viewBox="0 0 10 6" className="w-2.5 h-2" aria-hidden="true">
  <path d={up ? "M0 6 L5 0 L10 6 Z" : "M0 0 L5 6 L10 0 Z"} fill="currentColor"/>
</svg>;

// A number with up/down arrows; going below 1 takes the label out of the basket
export default function LabelQuantitySelector ({ service, charityId, tier, recipientId, label = "labels", borderClass = "border-dpGreenDeepest", className, ...otherProps }: LabelQuantitySelectorProps) {
  const isOutbound = !!recipientId;
  const { basket, addLabel, removeLabel, addOutboundLabel, removeOutboundLabel } = useBasket();
  const currentValue = (!isOutbound && isInboundServiceName(service))
    ? basket.labels[service][charityId][tier]
    :
    (isOutbound && recipientId && isOutboundServiceName(service))
      ? basket.outboundLabels[service][recipientId].labels[tier]
      : 0;

  const arrowClasses = "flex items-center justify-center w-7 h-6 text-dpTextBlack hover:bg-dpGrayLight disabled:opacity-40 focus-visible:outline focus-visible:outline-2";

  return <div
    role="group"
    aria-label={`Number of ${label}`}
    className={`inline-flex flex-row items-center gap-3 rounded-lg border-1 bg-white pl-4 pr-2 py-2 ${borderClass} ${className || ""}`}
    { ...otherProps }
  >
    <output aria-live="polite" className="min-w-[2ch] text-center text-lg">{currentValue}</output>
    <div className={`flex flex-col rounded-md border-1 overflow-hidden ${borderClass}`}>
      <button
        type="button"
        aria-label="Add one more label"
        className={`${arrowClasses} border-b-1 ${borderClass}`}
        onClick={() => {
          if (!isOutbound && isInboundServiceName(service)) {
            addLabel(service, charityId, tier);
          }

          if (isOutbound && recipientId && isOutboundServiceName(service)) {
            addOutboundLabel(service, recipientId, tier);
          }
        }}
        disabled={currentValue >= MAX_LABELS}
      >
        <Arrow up />
      </button>
      <button
        type="button"
        aria-label={currentValue <= 1 ? "Remove this label from basket" : "Remove one label"}
        className={arrowClasses}
        onClick={() => {
          if (!isOutbound && isInboundServiceName(service)) {
            removeLabel(service, charityId, tier);
          }
          if (isOutbound && recipientId && isOutboundServiceName(service)) {
            removeOutboundLabel(service, recipientId, tier);
          }
        }}
      >
        <Arrow />
      </button>
    </div>
  </div>;
};
